"use client";

import { useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { useTranslations } from "next-intl";
import { PhotoProvider } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { USER_GUIDE_PDF_PATHS, type UserGuideDocKey } from "../../constants/userGuideDocs";
import GameCreationGuide from "./gameCreation";
import HowToUseLiveDashboardGuide from "./howToUseLiveDashboard";
import HowToUseLivePerformanceGuide from "./howToUseLivePerformance";
import LessonSequenceGuide from "./lessonSequence";

type UserGuidePdfViewerProps = {
    docKey: UserGuideDocKey;
    onClose: () => void;
};

export default function UserGuidePdfViewer({ docKey, onClose }: UserGuidePdfViewerProps) {
    const tMain = useTranslations("main");
    const title = tMain(docKey);
    const pdfSrc = USER_GUIDE_PDF_PATHS[docKey];

    useEffect(() => {
        const scrollY = window.scrollY;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;
        const previousBodyPosition = document.body.style.position;
        const previousBodyTop = document.body.style.top;
        const previousBodyWidth = document.body.style.width;

        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";

        return () => {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
            document.body.style.position = previousBodyPosition;
            document.body.style.top = previousBodyTop;
            document.body.style.width = previousBodyWidth;
            window.scrollTo(0, scrollY);
        };
    }, []);

    return (
        <div className="fixed inset-0 top-16 z-70 flex overscroll-none lg:left-60">
            <button
                type="button"
                onClick={onClose}
                className="absolute inset-0 bg-black/25"
                aria-label={tMain("close")}
            />
            <section className="relative z-10 m-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-2xl sm:m-4">
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 sm:px-6">
                    <h2 className="text-base font-semibold text-neutral-800 sm:text-lg">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100"
                        aria-label={tMain("close")}
                    >
                        <IoClose size={20} />
                    </button>
                </div>
                {docKey === "gameCreation" ? (
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white">
                        <PhotoProvider maskOpacity={0.85}>
                            <GameCreationGuide />
                        </PhotoProvider>
                    </div>
                ) : docKey === "lessonSequence" ? (
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white">
                        <PhotoProvider maskOpacity={0.85}>
                            <LessonSequenceGuide />
                        </PhotoProvider>
                    </div>
                ) : docKey === "liveDashboard" ? (
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white">
                        <PhotoProvider maskOpacity={0.85}>
                            <HowToUseLiveDashboardGuide />
                        </PhotoProvider>
                    </div>
                ) : docKey === "livePerformance" ? (
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white">
                        <PhotoProvider maskOpacity={0.85}>
                            <HowToUseLivePerformanceGuide />
                        </PhotoProvider>
                    </div>
                ) : (
                    <iframe
                        src={pdfSrc}
                        title={title}
                        className="min-h-0 w-full flex-1 border-0 bg-neutral-50"
                    />
                )}
            </section>
        </div>
    );
}
